import { IDBProgressTracking, IUserProgress, useAddUnit, useAddUsersToUnit, useGetPaginatedUsers, useGetProgress, useGetUnitsByGroup, useGroups } from "@/hooks"; 
import { IEdges } from "@/interfaces";
import { useToast } from "@netfront/ekardo-content-library";
import { IDBUnit, IMembership, useSendForgotPassword } from "@netfront/gelada-identity-library";
import { useEffect, useState } from "react";
import { UserProgressTableProps } from "./UserProgressTable.interfaces";
import { BorderedCircle, BulkActionHandler, CalendarIcon, Checkbox, Dialog, FlexContainer, Input, IOption, Popover, PopoverContent, Select, SkeletonLoader } from "@netfront/ui-library";
import { useToggle } from "@netfront/common-library";
import { StudentProfileForm } from "@/components/Forms";
import { ECrudOperation } from "@/interfaces/enums";
import { format } from "date-fns";
import { getFriendlyStatus, getUserFlowStatusVariant, getVariantByDates } from "./UserProgressTable.helpers";
import { ExportActions } from "./ExportActions";

const formatDateTime = (date: string | Date) => {
  const dateObj = new Date(String(date));
  return (
    <>
      {format(dateObj, 'yyyy-MM-dd')}
      <br />
      {format(dateObj, 'HH:mm:ss')}
    </>
  );
};

const ModuleProgress = ({ progress }: { progress: IDBProgressTracking }) => {
  const { status, firstTracking, name } = progress;
  const { started, startedDate, completed, completedDate, id } = firstTracking ?? {};

  const label = String(name ?? '')
    .replace(/^Lesson\s*/i, '')
    .trim() || '?';

  return (
    <Popover
      trigger={
        <BorderedCircle
          value={label}
          variant={getUserFlowStatusVariant(status)}
        />
      }
    >
      <PopoverContent title={name}>
        {!firstTracking ? (
          <dl className="c-progress-tracker-details">
            <CalendarIcon className={`c-icon c-progress-tracker-details__icon c-progress-tracker-details__icon--purple`} />
            <div className="c-progress-tracker__item">
              <dt className="c-progress-tracker__item-title">Started</dt>
              <dd>{getFriendlyStatus('AVAILABLE')}</dd>
            </div>
          </dl>
        ): (
          <>
            <dl key={id} className="c-progress-tracker-details">
              <CalendarIcon className={`c-icon c-progress-tracker-details__icon c-progress-tracker-details__icon--${getVariantByDates({ started, completed })}`} />
              <div className="c-progress-tracker__item">
                <dt className="c-progress-tracker__item-title">Started</dt>
                <dd>
                  {startedDate ? formatDateTime(startedDate) : getFriendlyStatus('AVAILABLE')}
                </dd>
              </div>
              {
                started && (
                  <>
                    <span>-</span>
                    <div className="c-progress-tracker__item">
                      <dt className="c-progress-tracker__item-title">Completed</dt>
                      <dd>
                        {completedDate ? formatDateTime(completedDate) : getFriendlyStatus('IN_PROGRESS')}
                      </dd>
                    </div>
                  </>
                )
              }
            </dl>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
};

export const UserProgressTable = ({ groupId, userFlowId, userTypeId }: UserProgressTableProps) => {
  const { group } = useGroups();
  const { handleToastError, handleToastSuccess } = useToast();
  const { isToggled: isUnitsDialogOpen, toggle: toggleUnitsDialogOpen } = useToggle();
  const { isToggled: isResetPasswordDialogOpen, toggle: toggleResetPasswordDialogOpen } = useToggle();
  const { isToggled: isProfileDialogOpen, toggle: toggleProfileDialogOpen } = useToggle();

  const [users, setUsers] = useState<IEdges<IMembership>[]>([]);
  const [userTrackings, setUserTrackings] = useState<IUserProgress[]>([]);
  const [units, setUnits] = useState<IDBUnit[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<IMembership[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<IDBUnit | undefined>();
  const [newUnitName, setNewUnitName] = useState<string>('');
  const [bulkActionUnitState, setBulkActionUnitState] = useState<ECrudOperation>(ECrudOperation.Update);
  const [filterUnitId, setFilterUnitId] = useState<string>('');

  const { handleGetPaginatedUsers } = useGetPaginatedUsers({
    onCompleted: ({ connection }) => {
      const returnedUsers = connection.edges.map(({ node }) => node);
      setUsers(connection.edges);

      const usersId = returnedUsers.map(({ user }) => Number(user?.id));
      
      handleGetProgress({ 
        usersId, 
        userFlowId 
      });
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const { handleGetUnitsByGroup } = useGetUnitsByGroup({
    onCompleted: ({ units: returnedUnits }) => {
      setUnits(returnedUnits);
      if(returnedUnits.length === 0) {
        setBulkActionUnitState(ECrudOperation.Create);
      }
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const { handleGetProgress, isLoading: isGetProgressLoading = false } = useGetProgress({
    onCompleted: ({ progressTrackings }) => {
      setUserTrackings(progressTrackings);
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const { handleAddUnit } = useAddUnit({
    onCompleted: ({ unit }) => {
      setUnits([...units, unit]);
  
      handleAddUsersToUnit({
        unitId: unit.id,
        userIds: selectedUsers
          .filter(m => m.user)
          .map(m => Number(m.user!.id)),
      });
  
      setUsers(prevUsers =>
        prevUsers.map(edge => {
          const membership = edge.node;
          if (
            selectedUsers.some(
              selected => String(selected.user?.id) === String(membership.user?.id)
            )
          ) {
            return {
              ...edge,
              node: {
                ...membership,
                unit,
              },
            };
          }
          return edge;
        }) as IEdges<IMembership>[]
      );
  
      setNewUnitName('');
      setSelectedUnit(undefined);
      setSelectedUsers([]);
      handleToastSuccess({ 
        message: 'Unit created and users assigned successfully' 
      });
    },
    onError: (error) => {
      setNewUnitName('');
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const { handleAddUsersToUnit } = useAddUsersToUnit({
    onCompleted: () => {
      setUsers(prevUsers =>
        prevUsers.map(edge => {
          const membership = edge.node;
          if (
            selectedUsers.some(
              selected => String(selected.user?.id) === String(membership.user?.id)
            )
          ) {
            return {
              ...edge,
              node: {
                ...membership,
                unit: selectedUnit,
              },
            };
          }
          return edge;
        }) as IEdges<IMembership>[]
      );
      setSelectedUnit(undefined);
      setSelectedUsers([]);
      setBulkActionUnitState(ECrudOperation.Update);
      handleToastSuccess({
        message: 'Successfully updated'
      });
      toggleUnitsDialogOpen();
    },
    onError: (error) => {
      setSelectedUnit(undefined);
      setSelectedUsers([]);
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
      toggleUnitsDialogOpen();
    },
  });

  const { handleSendForgotPassword } = useSendForgotPassword({
    onCompleted: () => {
      setSelectedUsers([]);
      handleToastSuccess({
        message: 'Password reset sent'
      });
      toggleResetPasswordDialogOpen();
    },
    onError: (error) => {
      setSelectedUsers([]);
      toggleResetPasswordDialogOpen();
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  useEffect(() => {
    if (!groupId) return;

    handleGetPaginatedUsers({
      groupId,
      userTypeId
    });

    handleGetUnitsByGroup({
      groupId
    })
  }, [group]);

  const handleUserSelectChange = (userId: string) => {
    const membership = users.find(({ node: { user } }) => String(user?.id) === userId)?.node;

    setSelectedUsers(prevSelected => {
      const alreadySelected = prevSelected.some(m => String(m.user?.id) === userId);
      if (alreadySelected) {
        return prevSelected.filter(m => String(m.user?.id) !== userId);
      } else {
        return membership ? [...prevSelected, membership] : prevSelected;
      }
    });
  };

  const filteredUserTrackings = filterUnitId
    ? userTrackings.filter(({ userId }) => {
        const membership = users.find(({ node: { user } }) => user?.id === userId)?.node;
        return String(membership?.unit?.id) === filterUnitId;
      })
    : userTrackings;

  const addNewUnitOption = {
    id: 'new-class', 
    name: 'Add new class', 
    value: 'new-class'
  } as IOption;

  return isGetProgressLoading ? (
    <div className="grid gap-3">
      <SkeletonLoader width="100%" height="4rem" />
      <SkeletonLoader width="100%" height="4rem" />
      <SkeletonLoader width="100%" height="4rem" />
    </div>
  ) : (
    <>
      <BulkActionHandler
        selectedIds={selectedUsers.map(m => String(m.user?.id))}
        bulkActions={[
          { id: 0, label: 'Assign class', action: toggleUnitsDialogOpen },
        ]}
        toggleAllRowsSelected={isSelected => {
          if (isSelected) {
            const allMemberships = users.map(({ node }) => node as IMembership);
            setSelectedUsers(allMemberships);
          } else {
            setSelectedUsers([]);
          }
        }}
        bulkActionPlaceHolder="Select action"
        name="student-management-bulk-actions"
      />

      <ExportActions
        filterUnitId={filterUnitId}
        units={units}
        users={users}
        userTrackings={filteredUserTrackings}
        onFilterChange={setFilterUnitId}
      />

      <div className="-mx-8">
        <table className="pb-12 px-8">
          <thead>
            <tr>
              <th>
                Select
                <span className="sr-only">Select user for bulk action</span>
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>Class</th>
              <th>Modules</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUserTrackings.map(({ progress: unsortedProgress, userId }) => {
              const progress = [...unsortedProgress].sort((a, b) => (a.userFlowStepId ?? 0) - (b.userFlowStepId ?? 0));
              const membership = users.find(({ node: { user } }) => user?.id === userId)?.node as IMembership;
              const isChecked = selectedUsers.some(m => String(m.user?.id) === String(userId));
              return (
                <tr key={userId}>
                  <td className="no-kanzi-pdf">
                    <Checkbox
                      id={String(membership?.user?.id)}
                      labelText="Select user"
                      name={String(membership?.user?.id)}
                      value={String(membership?.user?.id)}
                      onChange={() => handleUserSelectChange(String(userId))}
                      isChecked={isChecked}
                      isLabelHidden
                    />
                  </td>
                  <td>{`${membership?.user?.firstName} ${membership?.user?.lastName}`}</td>
                  <td>{membership?.user?.credential?.email}</td>
                  <td>{membership?.unit?.name}</td>
                  <td>
                    <FlexContainer gap="x-small" >
                      {progress.map((tracking, index) => {
                        return <ModuleProgress key={`tracking-${index + 1}`} progress={tracking} />
                      })}
                    </FlexContainer>
                  </td>
                  <td>
                    <div className="flex items-center no-kanzi-pdf">
                      <button 
                        className="flex items-center justify-center bg-transparent border-none p-0 pointer mr-4"
                        onClick={() => {
                          setSelectedUsers([membership]);
                          toggleProfileDialogOpen();
                        }}
                        title="View profile"
                      >
                        <span className="sr-only">
                          Access profile
                        </span>
                        <img 
                          src="/images/access-profile-icon.svg" 
                          alt="Profile" 
                        />
                      </button>
                      <button 
                        className="flex items-center justify-center bg-transparent border-none p-0 pointer"
                        title="Reset password"
                        onClick={() => {
                          setSelectedUsers([membership]);
                          toggleResetPasswordDialogOpen();
                        }}
                      >
                        <span className="sr-only">
                          Reset password
                        </span>
                        <img 
                          src="/images/reset-password-icon.svg" 
                          alt="Profile" 
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog
        isOpen={isUnitsDialogOpen}
        title={`Assign students to a class`}
        onClose={() => {
          toggleUnitsDialogOpen();
          setBulkActionUnitState(ECrudOperation.Update);
        }}
        isConfirmDisabled={bulkActionUnitState === ECrudOperation.Create ? newUnitName === '' : !selectedUnit}
        onConfirm={() => {
          if(bulkActionUnitState === ECrudOperation.Create) {
            handleAddUnit({
              groupId: Number(group?.id),
              unitName: String(newUnitName)
            })
          } else {
            handleAddUsersToUnit({
              unitId: Number(selectedUnit?.id),
              userIds: selectedUsers
                .filter(m => m.user)
                .map(m => Number(m.user!.id)),
            });
          }
        
        }}
        isNarrow
      >
        <p>
          You have currently selected {selectedUsers.length} student{selectedUsers.length > 1 ? `'s` : ''}
        </p>
        <div className="max-w-52 mx-auto mt-8">
          {bulkActionUnitState === ECrudOperation.Update && (
            <>
              <Select
                additionalClassNames="w-full"
                id="units"
                labelText="Available classes"
                name="units"
                onChange={({ currentTarget: { value } }) => {
                  if(value === addNewUnitOption.value) {
                    setBulkActionUnitState(ECrudOperation.Create);
                    return;
                  }
                  setSelectedUnit(units.find(u => u.id === Number(value)));
                }}
                options={[addNewUnitOption, ...units.map(({ id, name }) => ({ id, name, value: id }))]}
              />
            </>
          )}
        </div>

        {bulkActionUnitState === ECrudOperation.Create && (
          <div className="flex justify-center items-center">
            <Input
              id="add-unit"
              labelText="Add class"
              name="add-unit"
              type="text"
              onChange={({ currentTarget: { value } }) => setNewUnitName(value)}
              value={newUnitName}
            />
          </div>
        )}
      </Dialog>

      <Dialog
        isOpen={isResetPasswordDialogOpen}
        title="Send password reset"
        onClose={() => {
          setSelectedUsers([]);
          toggleResetPasswordDialogOpen();
        }}
        onConfirm={() => {
          handleSendForgotPassword({
            projectId: String(process.env.REACT_APP_PROJECT_ID), 
            userId: Number(selectedUsers[0]?.user?.id)
          });
        }}
        isNarrow
      >
        <div className="max-w-98 mx-auto">
          <Input
            id="reset-password"
            labelText="Email"
            name="reset-password"
            type="text"
            value={selectedUsers[0]?.user?.credential.email}
            isReadOnly
          />
        </div>
      </Dialog>

      <Dialog
        isOpen={isProfileDialogOpen}
        title="Profile"
        onClose={() => {
          setSelectedUsers([]);
          toggleProfileDialogOpen();
        }}
      >
        <div className="px-8">
          <StudentProfileForm
            groupId={Number(group?.id)}
            userId={Number(selectedUsers[0]?.user?.id)}
            onCompleted={(updatedUser) => {
              setUsers((prevUsers) =>
                prevUsers.map((edge) =>
                  edge.node.user?.id === updatedUser.id
                    ? { ...edge, node: { ...edge.node, user: updatedUser } }
                    : edge
                )
              );
        
              setSelectedUsers([]);
              toggleProfileDialogOpen();
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
